function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { createStatefulPromiseResolvers } from "../fetchUtils/statefulPromise";
import ObservableData from "./ObservableData";
class ObservableFragment extends ObservableData {
    get hasActiveConsumers() {
        return this.suspendedCount > 0 || this.observers.size > 0;
    }
    checkIfLostAllConsumers() {
        if (!this.hasActiveConsumers) {
            this.hasLostAllConsumersSinceFetch = true;
        }
    }
    scheduleConsumerCheck() {
        const REACT_COMMIT_GRACE_PERIOD_MS = 150;
        // Delay the check to give React time to render useFragment after Suspense resolves.
        // Without this, we'd check observers.size before useFragment has a chance to call subscribe().
        setTimeout(()=>this.checkIfLostAllConsumers(), REACT_COMMIT_GRACE_PERIOD_MS);
    }
    setLastFetchedBy(rootQueryId, succeeded) {
        if (succeeded) {
            this.lastFetchedByRootQueryId = rootQueryId;
            this.lastFetchSucceeded = true;
            if (this.hasActiveConsumers) {
                this.hasLostAllConsumersSinceFetch = false;
            }
        } else {
            this.lastFetchSucceeded = false;
        }
    }
    // Used to determine if the fragment has lost all consumers (in practical terms: whether
    // all useFragment hooks have unmounted) since  the last fetch done by a root query.
    // We use this to determine if we should send the fragment to the network when the root query is executed again.
    hasConsumerContinuityFor(currentRootQueryId) {
        return this.lastFetchedByRootQueryId === currentRootQueryId && this.lastFetchSucceeded && !this.hasLostAllConsumersSinceFetch;
    }
    clearLastFetchedBy() {
        this.lastFetchedByRootQueryId = null;
    }
    // Subscribe to future updates. This method does not emit the current value.
    subscribe(observer) {
        this.observers.add(observer);
        if (this.promise && this.promise.status === 'pending') {
            this.promise.resolve();
        }
        const watchCleanupFn = this.watch();
        return {
            unsubscribe: ()=>{
                this.observers.delete(observer);
                watchCleanupFn();
                this.checkIfLostAllConsumers();
            }
        };
    }
    notifyObservers(result) {
        var _this_promise;
        if ((result.data || result.errors) && ((_this_promise = this.promise) === null || _this_promise === void 0 ? void 0 : _this_promise.status) === 'pending') {
            this.promise.resolve();
        }
        super.notifyObservers(result);
    }
    startAsyncWaitOnResult(triggerRefetch) {
        this.suspendedCount++;
        if (this.promise && this.promise.status === 'pending') {
            this.promise.finally(()=>{
                this.suspendedCount--;
                this.scheduleConsumerCheck();
            });
            return this.promise;
        }
        this.promise = createStatefulPromiseResolvers();
        const watchCleanupFn = this.watch();
        if (this.lastFetchSucceeded && triggerRefetch) {
            // This means fetch succeeded once but since then data has dissappeared. Trigger a refetch to get data back in cache.
            triggerRefetch();
        }
        this.promise.finally(()=>{
            watchCleanupFn();
            this.suspendedCount--;
            this.scheduleConsumerCheck();
        });
        return this.promise;
    }
    constructor(...args){
        super(...args);
        // Promise that is synched with the latest available result returned from
        // the result store.
        _define_property(this, "promise", null);
        _define_property(this, "lastFetchedByRootQueryId", null);
        _define_property(this, "lastFetchSucceeded", false);
        _define_property(this, "hasLostAllConsumersSinceFetch", true);
        _define_property(this, "suspendedCount", 0);
    }
}
export { ObservableFragment as default };

//# sourceMappingURL=ObservableFragment.js.map